/**
 * ZeroClubWelcome (#2323)
 *
 * The zero-club landing rendered by HomeRedirect when the signed-in user
 * belongs to no clubs. Replaces the old AppHomePage fallback, whose
 * "Browse Clubs" hero pushed discover-first — the wrong funnel for an
 * invite-first product at current network size — and whose empty
 * "My Clubs" / "Upcoming Events" scaffolding was the ugliness.
 *
 * Dual hero (user decision 2026-07-15, feature doc
 * docs/canonical/features/future/landing-experience-redesign.md): two
 * equal-weight cards — join-with-invite (paste box) and start-a-club —
 * with discover demoted to a quiet tertiary link. The deliberate two-card
 * weight is a sanctioned deviation from the one-hero budget (ADR-UX-003):
 * the zero-club state genuinely has two personas (invited member who lost
 * the link / prospective leader) and no data to pick between them.
 *
 * The paste box accepts a full invite URL or a bare code; extraction +
 * plausibility live in lib/inviteCode.ts, and the live-validation rule
 * (`inviteInput`) delegates to the same extractor so the inline signal and
 * the submit decision cannot disagree. Existence/expiry is validated by
 * InviteAcceptPage after navigation — this screen never calls the API.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, Users, Compass } from 'lucide-react';
import Button from './Button';
import TopBar from './TopBar';
import ValidatedInput from './ValidatedInput';
import { extractInviteCode } from '../lib/inviteCode';

export default function ZeroClubWelcome() {
  const navigate = useNavigate();
  const [inviteValue, setInviteValue] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleJoinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = extractInviteCode(inviteValue);
    if (!code) {
      setSubmitError("That doesn't look like an invite link or code. Check it and try again.");
      return;
    }
    navigate(`/invite/${code}`);
  };

  return (
    <>
      {/* TopBar keeps app chrome present — on md+ the BottomNav is hidden and
          the TopBar carries the desktop nav + avatar, so without it a
          zero-club user would be stranded with no way to reach settings or
          sign out (Frank round 1, PR #2331). pt-14 offsets its fixed h-14. */}
      <TopBar title="Welcome" />
      <div className="pt-14">
      <div className="px-4 py-6 max-w-md mx-auto space-y-6">
      <header className="space-y-2 pt-4">
        <h1 className="text-2xl font-bold text-text-primary">Welcome to ORO move™</h1>
        <p className="text-text-secondary">
          ORO runs on clubs — most people join through an invite from their club leader.
        </p>
      </header>

      {/* Dual hero card 1: join with an invite */}
      <section className="bg-oro-surface border border-oro-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Ticket className="w-5 h-5 text-accent" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-text-primary">Join with an invite</h2>
        </div>
        <p className="text-sm text-text-secondary">
          Got an invite link or code? Paste it here and you're in.
        </p>
        <form onSubmit={handleJoinSubmit} noValidate className="space-y-3">
          <ValidatedInput
            validate="inviteInput"
            value={inviteValue}
            onChange={(value) => {
              setInviteValue(value);
              setSubmitError(null);
            }}
            placeholder="Invite link or code"
            aria-label="Invite link or code"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            // Submit error owns messaging — the live rule message would
            // stack a second, differently-worded red line under the same
            // input (Frank round 1 P3). Live status icon still shows.
            showMessage={false}
          />
          {submitError && (
            <p role="alert" className="text-sm text-oro-error">
              {submitError}
            </p>
          )}
          <Button type="submit" variant="primary" className="w-full">
            Join your club
          </Button>
        </form>
      </section>

      {/* Dual hero card 2: start a club */}
      <section className="bg-oro-surface border border-oro-border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-accent" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-text-primary">Start a club</h2>
        </div>
        <p className="text-sm text-text-secondary">
          Leading a crew? Create your club and invite your people in minutes.
        </p>
        <Button
          variant="secondary"
          className="w-full"
          onClick={() => navigate('/clubs/create')}
        >
          Create a club
        </Button>
      </section>

      {/* Tertiary, deliberately quiet: discover is not the default funnel */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => navigate('/clubs')}
          className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-accent oro-transition min-h-11 px-3"
        >
          <Compass className="w-4 h-4" aria-hidden="true" />
          Browse public clubs
        </button>
      </div>
      </div>
      </div>
    </>
  );
}
